import { ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material/styles';
import { dsV2 } from '../../ds-v2-tokens';

/**
 * Chip du Design System V2 (TEC-3051) — valeurs exactes de la maquette (`.chip`) :
 * wash doux + texte solide, sans bordure, pill (radius 20), 11px/600. Tons sémantiques
 * alignés sur la charte. Composant autonome (le `Tag` historique reste inchangé).
 */
export type ChipV2Tone = 'neutral' | 'info' | 'success' | 'warning' | 'error' | 'accent';
export type ChipV2Size = 'small' | 'medium';

export type ChipV2Props = {
  label: ReactNode;
  tone?: ChipV2Tone;
  size?: ChipV2Size;
  icon?: ReactNode;
  /** Affiche les chiffres en tabular-nums (montants, compteurs). */
  numeric?: boolean;
  sx?: SxProps<Theme>;
};

const toneColors = (tone: ChipV2Tone): { bg: string; fg: string } => {
  switch (tone) {
    case 'info':
      return { bg: dsV2.color.bleuDoux, fg: dsV2.color.bleuDark };
    case 'success':
      return { bg: dsV2.color.vertLight, fg: dsV2.color.vert };
    case 'warning':
      return { bg: dsV2.color.amberLight, fg: dsV2.color.amberText };
    case 'error':
      return { bg: dsV2.color.rougeLight, fg: dsV2.color.rougeDark };
    case 'accent':
      return { bg: dsV2.color.violetLight, fg: dsV2.color.violet };
    case 'neutral':
    default:
      return { bg: dsV2.color.surface2, fg: dsV2.color.t3 };
  }
};

const StyledChipV2 = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'dsSize' && prop !== 'numeric',
})<{ tone: ChipV2Tone; dsSize: ChipV2Size; numeric?: boolean }>(({ tone, dsSize, numeric }) => {
  const { bg, fg } = toneColors(tone);
  const small = dsSize === 'small';
  return {
    height: 'auto',
    borderRadius: dsV2.radius.pill,
    border: 'none',
    backgroundColor: bg,
    color: fg,
    '& .MuiChip-label': {
      padding: small ? '2px 9px' : '3px 10px',
      fontFamily: dsV2.font.sansSemiBold,
      fontWeight: 600,
      fontSize: small ? 10 : 11,
      lineHeight: 1.45,
      color: fg,
      whiteSpace: 'nowrap',
      fontVariantNumeric: numeric ? 'tabular-nums' : 'normal',
    },
    '& .MuiChip-icon': { color: fg, marginLeft: small ? 6 : 8, marginRight: -5, fontSize: 12 },
  };
});

const ChipV2 = ({ label, tone = 'neutral', size = 'medium', icon, numeric, sx }: ChipV2Props) => (
  <StyledChipV2
    label={label}
    icon={icon as React.ReactElement | undefined}
    tone={tone}
    dsSize={size}
    numeric={numeric}
    sx={sx}
  />
);

export default ChipV2;
export { ChipV2 };
